import { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, ChevronDown, Mail, Sparkles, X } from "lucide-react";
import { faqsData, faqCategories, FAQ, FAQCategory } from "../data/faqsData";

const FAQs = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [openFAQs, setOpenFAQs] = useState<Set<string>>(new Set());

  // Filter FAQs based on search and category
  const filteredFAQs = useMemo(() => {
    return faqsData.filter((faq) => {
      const matchesSearch =
        searchQuery === "" ||
        faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesCategory =
        selectedCategory === "all" || faq.category === selectedCategory;

      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, selectedCategory]);

  // Group FAQs by category for display
  const groupedFAQs = useMemo(() => {
    const groups: Record<string, FAQ[]> = {};
    filteredFAQs.forEach((faq) => {
      if (!groups[faq.category]) {
        groups[faq.category] = [];
      }
      groups[faq.category].push(faq);
    });
    return groups;
  }, [filteredFAQs]);

  const toggleFAQ = (id: string) => {
    setOpenFAQs((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const getCategoryInfo = (categoryId: string): FAQCategory | undefined => {
    return faqCategories.find((cat) => cat.id === categoryId);
  };

  const clearSearch = () => {
    setSearchQuery("");
    setSelectedCategory("all");
  };

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative pt-16 pb-20 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#8B52FF]/5 via-white to-[#E05A6A]/5" />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%238B52FF' fill-opacity='1'%3E%3Ccircle cx='30' cy='30' r='1.5'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        {/* Gradient Orbs */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#8B52FF]/20 rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#E05A6A]/20 rounded-full blur-3xl" />

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white shadow-md border border-gray-100 mb-8"
          >
            <Sparkles className="w-4 h-4 text-[#8B52FF]" />
            <span className="text-sm font-medium text-gray-700">
              {faqsData.length}+ Questions Answered
            </span>
          </motion.div>

          {/* Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6"
          >
            Frequently Asked{" "}
            <span className="bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] bg-clip-text text-transparent">
              Questions
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-lg md:text-xl text-gray-600 mb-10 max-w-2xl mx-auto"
          >
            Everything you need to know about Unimerg. Can't find the answer
            you're looking for? Reach out to our support team.
          </motion.p>

          {/* Search Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="relative max-w-2xl mx-auto"
          >
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search for answers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-12 py-4 rounded-2xl border border-gray-200 bg-white shadow-lg focus:outline-none focus:ring-2 focus:ring-[#8B52FF]/20 focus:border-[#8B52FF] transition-all text-gray-900 placeholder-gray-400"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-4 h-4 text-gray-400" />
                </button>
              )}
            </div>

            {/* Quick suggestion when typing */}
            {searchQuery && filteredFAQs.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-2 text-left text-sm text-gray-500">
                Found {filteredFAQs.length} result
                {filteredFAQs.length !== 1 ? "s" : ""}
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="sticky top-16 lg:top-20 z-30 bg-white/80 backdrop-blur-lg border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
            <button
              onClick={() => setSelectedCategory("all")}
              className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                selectedCategory === "all"
                  ? "bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white shadow-md"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              All Topics
            </button>
            {faqCategories.map((category) => {
              const Icon = category.icon;
              const count = faqsData.filter(
                (f) => f.category === category.id
              ).length;
              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedCategory === category.id
                      ? "bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white shadow-md"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{category.name}</span>
                  <span
                    className={`text-xs ${
                      selectedCategory === category.id
                        ? "text-white/70"
                        : "text-gray-400"
                    }`}
                  >
                    ({count})
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredFAQs.length === 0 ? (
            /* Empty State */
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-16"
            >
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#8B52FF]/10 to-[#E05A6A]/10 flex items-center justify-center">
                <Search className="w-8 h-8 text-[#8B52FF]" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                No results found
              </h3>
              <p className="text-gray-600 mb-6">
                We couldn't find any FAQs matching "{searchQuery}"
              </p>
              <button
                onClick={clearSearch}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white font-medium hover:shadow-lg transition-all"
              >
                Clear Search
              </button>
            </motion.div>
          ) : selectedCategory === "all" && !searchQuery ? (
            /* Grouped by Category View */
            <div className="space-y-12">
              {faqCategories.map((category) => {
                const categoryFAQs = groupedFAQs[category.id];
                if (!categoryFAQs || categoryFAQs.length === 0) return null;

                const Icon = category.icon;

                return (
                  <motion.div
                    key={category.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                  >
                    {/* Category Header */}
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-2 rounded-xl bg-gradient-to-br from-[#8B52FF]/10 to-[#E05A6A]/10">
                        <Icon className="w-5 h-5 text-[#8B52FF]" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-gray-900">
                          {category.name}
                        </h2>
                        <p className="text-sm text-gray-500">
                          {category.description}
                        </p>
                      </div>
                    </div>

                    {/* FAQ Items */}
                    <div className="space-y-3">
                      {categoryFAQs.map((faq, index) => (
                        <FAQItem
                          key={faq.id}
                          faq={faq}
                          isOpen={openFAQs.has(faq.id)}
                          onToggle={() => toggleFAQ(faq.id)}
                          index={index}
                        />
                      ))}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            /* Filtered/Search Results View */
            <div className="space-y-3">
              {filteredFAQs.map((faq, index) => {
                const categoryInfo = getCategoryInfo(faq.category);
                return (
                  <FAQItem
                    key={faq.id}
                    faq={faq}
                    isOpen={openFAQs.has(faq.id)}
                    onToggle={() => toggleFAQ(faq.id)}
                    index={index}
                    showCategory
                    categoryInfo={categoryInfo}
                  />
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Contact CTA Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#8B52FF]/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#E05A6A]/20 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Still have questions?
            </h2>
            <p className="text-lg text-gray-300 mb-10 max-w-xl mx-auto">
              Can't find the answer you're looking for? Our support team is here
              to help you succeed.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              {/* <Link
                to="/contact"
                className="group inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white font-semibold hover:shadow-xl hover:shadow-[#8B52FF]/25 transition-all"
              >
                <MessageCircle className="w-5 h-5" />
                Contact Support
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link> */}

              <a
                href="mailto:support@unimerg.com"
                className="group inline-flex items-center gap-2 px-8 py-4 rounded-xl border-2 border-white/20 text-white font-semibold hover:bg-white/10 transition-all"
              >
                <Mail className="w-5 h-5" />
                support@unimerg.com
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

/* FAQ Item Component */
interface FAQItemProps {
  faq: FAQ;
  isOpen: boolean;
  onToggle: () => void;
  index: number;
  showCategory?: boolean;
  categoryInfo?: FAQCategory;
}

const FAQItem = ({
  faq,
  isOpen,
  onToggle,
  index,
  showCategory,
  categoryInfo,
}: FAQItemProps) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.03 }}
      className={`bg-white rounded-2xl border transition-all ${
        isOpen
          ? "border-[#8B52FF]/30 shadow-lg shadow-[#8B52FF]/5"
          : "border-gray-100 hover:border-gray-200 hover:shadow-md"
      }`}
    >
      <button
        onClick={onToggle}
        className="w-full px-6 py-5 flex items-start gap-4 text-left"
      >
        <div
          className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
            isOpen
              ? "bg-gradient-to-br from-[#8B52FF] to-[#E05A6A]"
              : "bg-gray-100"
          }`}
        >
          <ChevronDown
            className={`w-4 h-4 transition-transform ${
              isOpen ? "rotate-180 text-white" : "text-gray-500"
            }`}
          />
        </div>
        <div className="flex-1">
          {showCategory && categoryInfo && (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-[#8B52FF] mb-1">
              <categoryInfo.icon className="w-3 h-3" />
              {categoryInfo.name}
            </span>
          )}
          <h3
            className={`font-semibold transition-colors ${
              isOpen ? "text-[#8B52FF]" : "text-gray-900"
            }`}
          >
            {faq.question}
          </h3>
        </div>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-5 pl-18">
              <div className="pl-12">
                <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default FAQs;
